// MUI-free copy of Ui-components/ios-development/IosChallenges.
import {memo} from 'react';
import type {StaticImageData} from 'next/image';
import Container from '@/components/atoms/Container';
import ArrowButton from '@/components/atoms/ArrowButton';
import SectionHeader from '@/components/molecules/SectionHeader';
import OptimizedImage from '@/components/atoms/OptimizedImage';
import Icon from '@/components/atoms/Icon';
import styles from './IosChallenges.module.scss';

interface ChallengePoint {
    title: string;
    description: string;
}

interface ImageStat {
    value: string;
    label: string;
    description: string;
}

interface IosChallengesProps {
    heading: string;
    subheading: string;
    imageSrc: string | StaticImageData;
    imageAlt: string;
    /** Figures rendered in boxed cards over the lower part of the image. */
    imageStats?: ImageStat[];
    pointsTitle: string;
    points: ChallengePoint[];
    ctaText: string;
    onCtaClick?: () => void;
}

const COVER = {objectFit: 'cover'} as const;

const IosChallenges = ({
    heading,
    subheading,
    imageSrc,
    imageAlt,
    imageStats,
    pointsTitle,
    points,
    ctaText,
    onCtaClick
}: IosChallengesProps) => (
    <div>
        <Container>
            <SectionHeader heading={heading} subheading={subheading} className={styles.headerWrap} />

            <div className={styles.card}>
                <div className={`${styles.item} ${styles.imageCol}`}>
                    <div className={imageStats?.length ? `${styles.imageWrap} ${styles.imageWrapWithStats}` : styles.imageWrap}>
                        <OptimizedImage src={imageSrc} alt={imageAlt} fill sizes="(max-width: 900px) 100vw, 50vw" style={COVER} />
                        {!!imageStats?.length && (
                            <div className={styles.statsOverlay}>
                                {imageStats.map((stat) => (
                                    <div key={stat.label} className={styles.statBox}>
                                        <p className={`font-headers ${styles.statValue}`}>{stat.value}</p>
                                        <p className={`font-headers ${styles.statLabel}`}>{stat.label}</p>
                                        <p className={`font-regular-text ${styles.statDesc}`}>{stat.description}</p>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>

                <div className={styles.item}>
                    <div className={styles.pointsPanel}>
                        <h3 className={`font-headers ${styles.panelTitle}`}>{pointsTitle}</h3>
                        <ul className={styles.pointList}>
                            {points?.map((point) => (
                                <li key={point.title} className={styles.pointItem}>
                                    <div className={styles.checkTile} aria-hidden="true">
                                        <Icon name="CheckRounded" className={styles.checkIcon} />
                                    </div>
                                    <div>
                                        <p className={`font-headers ${styles.pointTitle}`}>{point.title}</p>
                                        <p className={`font-regular-text ${styles.pointDesc}`}>{point.description}</p>
                                    </div>
                                </li>
                            ))}
                        </ul>
                        <div className={styles.ctaWrap}>
                            <ArrowButton variant="primary" icon="ArrowOutward" bounce={false} onClick={onCtaClick}>
                                {ctaText}
                            </ArrowButton>
                        </div>
                    </div>
                </div>
            </div>
        </Container>
    </div>
);

export default memo(IosChallenges);
